// Microbe RTS — Evolve sheet (slide-up bottom-sheet mutation shop).
//
// mountEvolve builds a hidden bottom-sheet listing MUTATIONS grouped by tier. Each card renders
// icon/name/blurb/cost and a state — buyable / owned / locked / keystone — derived from the sim
// (sim.offeredMutations + sim.canEvolve + colony genome/keystones). Buying pushes sim.command
// {t:'evolve'}. Keystones are irreversible, so committing one goes through a two-tap CONFIRM state.
//
// Per the bible ruling, the sim KEEPS RUNNING while the sheet is open — no pause command here.
//
// Cross-UI events (see hud.ts / input.ts headers):
//   IN 'microbe:evolve-open'      {}  -> open()
//   IN 'microbe:evolve-quickbuy'  {}  -> buy the recommended (first offered) mutation; if it's a
//                                        keystone, open + stage its confirm instead of firing blind.
//
// Element ids: #evolve-backdrop #evolve-sheet #evolve-list #evolve-close
// Class hooks: .evolve-open .mut-card .mut-buyable .mut-owned .mut-locked .mut-keystone .mut-confirm

import { MUTATIONS, SPECIATION_GATES } from '../config.js';
import type { Mutation } from '../config.js';
import type { Sim, Side } from '../types.js';
import { iconSvg, MUT_ICON } from './icons.js';

// SPECIATION_GATES is keyed by tier; used only to show a tier's entry requirement hint.
type GateTier = keyof typeof SPECIATION_GATES;

const ROMAN: Record<number, string> = { 1: 'I', 2: 'II', 3: 'III', 4: 'IV' };

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls?: string,
  id?: string,
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (id) e.id = id;
  return e;
}

function costText(cost: { energy: number; amino: number; toxin: number }): string {
  const bits = [`${iconSvg('energy')}${cost.energy}`, `${iconSvg('amino')}${cost.amino}`];
  if (cost.toxin > 0) bits.push(`${iconSvg('toxin')}${cost.toxin}`);
  return bits.join('  ');
}

export function mountEvolve(
  root: HTMLElement,
  sim: Sim,
  viewSide: Side = 'you',
): { open(): void; close(): void; refresh(): void } {
  let isOpen = false;
  // The keystone currently staged for an irreversible commit (two-tap confirm), if any.
  let pendingKeystoneId: string | null = null;
  // Tier-focused UI state: which tier tab is shown, and which sub-category (Units | Economy).
  // activeTier auto-snaps to the colony's tier the instant a speciation is bought (lastSeenTier
  // watches for the change); between speciations manual tab taps are respected.
  let activeTier = 1;
  let lastSeenTier = 1;
  let activeCategory: 'unit' | 'economy' = 'unit';
  // Tiers that actually exist in the data (drives the tab rail — no empty Tier-4 ghost).
  const TIERS = Array.from(new Set(MUTATIONS.map((m) => m.tier))).sort((a, b) => a - b);
  // Last-rendered content signature — we only rebuild the card list when it actually changes
  // (rebuilding every ~10/s refresh was destroying cards mid-tap, so taps only landed sometimes).
  let lastSig = '';

  const backdrop = el('div', 'evolve-backdrop', 'evolve-backdrop');
  const sheet = el('div', 'evolve-sheet', 'evolve-sheet');

  const header = el('div', 'evolve-header');
  const title = el('div', 'evolve-title');
  title.textContent = 'Evolve · Genome';
  const closeBtn = el('button', 'evolve-close', 'evolve-close');
  closeBtn.type = 'button';
  closeBtn.innerHTML = iconSvg('close');
  closeBtn.setAttribute('aria-label', 'Close');
  header.append(title, closeBtn);

  const tabs = el('div', 'evolve-tabs', 'evolve-tabs'); // tier rail
  const cats = el('div', 'evolve-cats', 'evolve-cats'); // Units | Economy toggle
  const list = el('div', 'evolve-list', 'evolve-list');
  sheet.append(header, tabs, cats, list);
  root.append(backdrop, sheet);

  // ---- state derivation ----
  const ownedLevel = (m: Mutation): number => {
    const col = sim.state.colonies[viewSide];
    if (col.keystones.includes(m.id)) return m.maxLevel;
    return col.genome[m.id] ?? 0;
  };
  const isOwnedFull = (m: Mutation): boolean => ownedLevel(m) >= m.maxLevel;
  // offeredMutations(side) already = (passes gates && affordable) for that side — the same predicate
  // as canEvolve, so scoping it to viewSide is byte-identical for solo AND correct for a PvP peer.
  const isBuyable = (m: Mutation): boolean =>
    !isOwnedFull(m) && sim.offeredMutations(viewSide).includes(m.id);

  function commit(m: Mutation) {
    sim.command({ t: 'evolve', mutationId: m.id });
    pendingKeystoneId = null;
    render();
  }

  function onCardTap(m: Mutation) {
    if (!isBuyable(m)) return;
    if (m.keystone) {
      if (pendingKeystoneId === m.id) {
        commit(m); // second tap: confirm irreversible commit
      } else {
        pendingKeystoneId = m.id; // first tap: stage confirm
        render();
      }
      return;
    }
    commit(m);
  }

  function card(m: Mutation): HTMLElement {
    const owned = isOwnedFull(m);
    const buyable = isBuyable(m);
    const confirming = pendingKeystoneId === m.id;
    const state = owned ? 'mut-owned' : buyable ? 'mut-buyable' : 'mut-locked';

    const c = el('button', `mut-card ${state}`);
    c.type = 'button';
    if (m.keystone) c.classList.add('mut-keystone');
    if (confirming) c.classList.add('mut-confirm');
    c.disabled = !buyable;

    const mIcon = el('span', 'mut-icon');
    mIcon.innerHTML = iconSvg(MUT_ICON[m.id] ?? 'evolve');

    const body = el('div', 'mut-body');
    const name = el('div', 'mut-name');
    const lvl =
      m.maxLevel > 1 && ownedLevel(m) > 0 ? ` (Lv ${ownedLevel(m)}/${m.maxLevel})` : '';
    name.textContent = `${m.name}${lvl}${m.keystone ? ' · keystone' : ''}`;
    const blurb = el('div', 'mut-blurb');
    blurb.textContent = m.blurb;
    body.append(name, blurb);

    const right = el('div', 'mut-right');
    const cost = el('div', 'mut-cost');
    if (owned) cost.textContent = 'Owned';
    else cost.innerHTML = costText(m.cost);
    const tag = el('div', 'mut-tag');
    if (owned) tag.innerHTML = iconSvg('check');
    else tag.textContent = confirming
      ? 'Commit? (irreversible)'
      : buyable
        ? m.keystone
          ? 'Keystone'
          : 'Buy'
        : 'Locked';
    right.append(cost, tag);

    c.append(mIcon, body, right);
    c.addEventListener('click', () => onCardTap(m));
    return c;
  }

  function tierGateHint(tier: number): string | null {
    if (!(tier in SPECIATION_GATES)) return null;
    const g = SPECIATION_GATES[tier as GateTier];
    return `enter: ${g.pop} pop · ${iconSvg('energy')}${g.energy} · ${iconSvg('amino')}${g.amino} · ${g.nodes} node(s)`;
  }

  function colTier(): number {
    return sim.state.colonies[viewSide].tier;
  }
  function isSpeciation(m: Mutation): boolean {
    return !!m.effect?.speciateToTier;
  }
  function categoryOf(m: Mutation): 'unit' | 'economy' {
    return m.category ?? 'unit';
  }
  // Nodes shown in a tier tab: that tier's own nodes, PLUS the speciation node that advances FROM
  // this tier (its data-tier is tier+1, but it's the "advance" action bought while still at `tier`).
  // Without pinning the advance node, "show current tier only" would hide the only way to progress.
  function nodesInTier(tier: number): Mutation[] {
    const advance = MUTATIONS.filter((m) => m.effect?.speciateToTier === tier + 1);
    const own = MUTATIONS.filter((m) => m.tier === tier && !isSpeciation(m));
    return [...advance, ...own];
  }
  // The speciation node bought while AT `tier` (its data-tier is tier+1).
  function advanceNodeFrom(tier: number): Mutation | undefined {
    return MUTATIONS.find((m) => m.effect?.speciateToTier === tier + 1);
  }
  function affordableCountIn(tier: number): number {
    let n = MUTATIONS.filter((m) => m.tier === tier && !isSpeciation(m) && isBuyable(m)).length;
    const adv = advanceNodeFrom(tier); // the Speciate action is the most important buyable — count it
    if (adv && isBuyable(adv)) n += 1;
    return n;
  }

  function contentSig(): string {
    // The focused tier + category MUST be in the sig, or the render guard early-returns on a tab tap
    // and the sheet looks frozen. colTier is in too so a speciation-driven snap re-renders.
    const parts: string[] = [pendingKeystoneId ?? '', `A${activeTier}`, `C${activeCategory}`, `T${colTier()}`];
    for (const m of MUTATIONS) parts.push(`${m.id}:${ownedLevel(m)}:${isBuyable(m) ? 1 : 0}`);
    return parts.join('|');
  }

  function buildTabs() {
    tabs.replaceChildren();
    const cur = colTier();
    for (const t of TIERS) {
      const tab = el('button', 'evolve-tab');
      tab.type = 'button';
      if (t === activeTier) tab.classList.add('is-active');
      if (t === cur) tab.classList.add('is-current');
      if (t > cur) tab.classList.add('is-locked');
      const label = el('span', 'evolve-tab-label');
      label.textContent = ROMAN[t] ?? `T${t}`;
      tab.append(label);
      const n = affordableCountIn(t);
      if (n > 0 && t !== activeTier) {
        const badge = el('span', 'evolve-tab-badge');
        badge.textContent = String(n);
        tab.append(badge);
      }
      tab.addEventListener('click', () => { activeTier = t; render(); });
      tabs.append(tab);
    }
  }

  function buildCats() {
    cats.replaceChildren();
    for (const c of ['unit', 'economy'] as const) {
      const b = el('button', 'evolve-cat');
      b.type = 'button';
      if (c === activeCategory) b.classList.add('is-active');
      b.textContent = c === 'unit' ? 'Units' : 'Economy';
      b.addEventListener('click', () => { activeCategory = c; render(); });
      cats.append(b);
    }
  }

  function render() {
    // Auto-snap focus to the colony's tier the instant a speciation lands (the hand-off).
    const cur = colTier();
    if (cur > lastSeenTier) activeTier = cur;
    lastSeenTier = cur;

    // Only rebuild when the visible state changed — otherwise the button under a finger gets
    // replaced mid-press and the tap is lost. Active tier/category/tier all live in the sig.
    const sig = contentSig();
    if (sig === lastSig && list.childElementCount > 0) return;
    lastSig = sig;

    buildTabs();
    buildCats();
    list.replaceChildren();

    const group = el('div', 'evolve-group');
    const nodes = nodesInTier(activeTier);

    // 1) speciation advance card(s) — always shown; the gate hint rides WITH the node it gates
    // (keyed by the node's target tier, so the requirement always sits next to the right button).
    for (const m of nodes.filter(isSpeciation)) {
      const target = m.effect?.speciateToTier;
      const hint = target != null ? tierGateHint(target) : null;
      if (hint) {
        const head = el('div', 'evolve-group-head');
        head.innerHTML = hint;
        group.append(head);
      }
      group.append(card(m));
    }
    // 2) keystones — always shown. Label as a lineage pick only when they're actually the
    // mutually-exclusive lineage keystones (tier III's lone off-lineage Aerobic is not a "pick one").
    const keys = nodes.filter((m) => m.keystone);
    if (keys.length) {
      const lineagePick = keys.length > 1 && keys.every((m) => m.lineage);
      const sub = el('div', 'evolve-subhead');
      sub.textContent = lineagePick ? 'Lineage · pick one' : 'Keystone';
      group.append(sub);
      for (const m of keys) group.append(card(m));
    }
    // 3) category-filtered trait cards.
    const traits = nodes.filter((m) => !isSpeciation(m) && !m.keystone && categoryOf(m) === activeCategory);
    const sub2 = el('div', 'evolve-subhead');
    sub2.textContent = activeCategory === 'unit' ? 'Unit traits' : 'Economy';
    group.append(sub2);
    if (traits.length) {
      for (const m of traits) group.append(card(m));
    } else {
      const empty = el('div', 'evolve-empty');
      empty.textContent = 'Nothing in this branch yet.';
      group.append(empty);
    }

    list.append(group);
  }

  function open() {
    isOpen = true;
    pendingKeystoneId = null;
    // Land on the colony's current tier every time the sheet opens.
    activeTier = colTier();
    lastSeenTier = activeTier;
    activeCategory = 'unit';
    backdrop.classList.add('evolve-open');
    sheet.classList.add('evolve-open');
    render();
  }
  function close() {
    isOpen = false;
    pendingKeystoneId = null;
    backdrop.classList.remove('evolve-open');
    sheet.classList.remove('evolve-open');
  }

  backdrop.addEventListener('click', close);
  closeBtn.addEventListener('click', close);

  const onOpen = () => open();
  const onQuickbuy = () => {
    const recId = sim.offeredMutations(viewSide)[0];
    if (!recId) return;
    const m = MUTATIONS.find((x) => x.id === recId);
    if (!m) return;
    if (m.keystone) {
      // Never fire an irreversible commit blind — surface it for confirmation. open() lands on the
      // colony's tier, so jump to the keystone's own tier or the confirm card would be off-screen.
      open();
      activeTier = m.tier;
      pendingKeystoneId = m.id;
      render();
    } else {
      commit(m);
    }
  };
  window.addEventListener('microbe:evolve-open', onOpen);
  window.addEventListener('microbe:evolve-quickbuy', onQuickbuy);

  render();

  return {
    open,
    close,
    refresh() {
      if (isOpen) render();
    },
    // Drop the window listeners when the match ends and the replay takes over.
    destroy() {
      window.removeEventListener('microbe:evolve-open', onOpen);
      window.removeEventListener('microbe:evolve-quickbuy', onQuickbuy);
    },
  };
}
